import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Reveal } from "@/lib/motion";
import { Chapter } from "./Chapter";
import { ABOUT, STATS, COUNTERS } from "@/data/content";

const Counter = ({ target }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return <span ref={ref}>{n}</span>;
};

const USIU_PHRASE = "United States International University – Africa";
const renderPara = (text) => {
  if (!text.includes(USIU_PHRASE)) return text;
  const [before, after] = text.split(USIU_PHRASE);
  return (
    <>
      {before}
      <a
        href="https://www.usiu.ac.ke/"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="about-usiu-link"
        className="text-[#b5423a] underline decoration-1 underline-offset-2 hover:text-[#2e7d6b] transition-colors duration-300"
      >
        {USIU_PHRASE}
      </a>
      {after}
    </>
  );
};

export const About = () => (
  <section id="about" className="relative z-10 py-24 md:py-32 lg:py-40">
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
      <Chapter num="01" title="About" kicker="Profile" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7 space-y-7">
          {ABOUT.map((p, i) => (
            <Reveal key={p} delay={i * 0.08}>
              <p className={`text-[#48606f] ${i === 0 ? "text-xl md:text-2xl font-serif-display font-light leading-snug" : "leading-relaxed text-[17px] md:text-[18px]"}`}>
                {renderPara(p)}
              </p>
            </Reveal>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-8">
            {COUNTERS.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.1}>
                <div className="border-t border-[#16324f] pt-4">
                  <div className="font-serif-display text-5xl md:text-6xl text-[#b5423a] leading-none">
                    <Counter target={c.num} />
                    {c.suffix}
                  </div>
                  <p className="mt-3 text-[14px] text-[#48606f] leading-snug">{c.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <ul className="border-t border-[#d2d5ce]">
              {STATS.map((s) => (
                <li
                  key={s.label}
                  className="flex items-center justify-between gap-4 py-3.5 border-b border-[#d2d5ce] group"
                >
                  <span className="font-serif-display text-[16.5px] text-[#16324f]">{s.label}</span>
                  <span className="font-mono-plex text-[13px] text-[#48606f] text-right group-hover:text-[#b5423a] transition-colors duration-300">
                    {s.label === "Institution" ? (
                      <a
                        href="https://www.usiu.ac.ke/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="about-institution-link"
                        className="text-[#b5423a] underline decoration-1 underline-offset-2 hover:text-[#2e7d6b] transition-colors duration-300"
                      >
                        {s.value}
                      </a>
                    ) : (
                      s.value
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
